import { queryOptions } from "@tanstack/react-query"
import type { QueryClient, UseMutationOptions } from "@tanstack/react-query"
import {
  CheckoutFailReason,
  ContactType,
  PromoFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import {
  BookingCalendarMini,
  InventoryTreeRateMini,
  ReservationMini,
  ReserveCalendarAvailabilityPOSTSuccessResponse,
  ReserveCheckoutPOSTSuccessResponse,
  ReserveConfirmPaymentPOSTSuccessResponse,
  ReserveConfirmSetupIntentPOSTSuccessResponse,
  ReservePaymentIntentPOSTSuccessResponse,
  ReservePromoPaymentPOSTSuccessResponse,
  ReserveSetupIntentPOSTSuccessResponse,
} from "ipctg-inflow-types/InFlowReserveAPI"

import api from "@/utilities/api"

import {
  ReserveCalendarAvailabilityError,
  ReserveCalendarRatesError,
  ReserveCalendarsError,
  ReserveCancelReservationError,
  ReserveCheckoutError,
  ReserveConfirmPaymentError,
  ReserveConfirmSetupIntentError,
  ReserveCurrentReservationsError,
  ReservePastReservationsError,
  ReservePaymentIntentError,
  ReservePlateError,
  ReservePlateUpdateError,
  ReservePromoError,
  ReserveSelectRateError,
  ReserveSetupIntentError,
} from "@/utilities/expectedErrors"

import {
  CALENDARS_BASE_KEY,
  CONFIRM_PAYMENT_BASE_KEY,
  CONFIRM_SETUP_INTENT_BASE_KEY,
  ME_BASE_KEY,
  PAYMENT_INTENT_BASE_KEY,
  PAYMENT_PROCESSOR_BASE_KEY,
  RESERVE_BASE_KEY,
  RESERVE_CALENDAR_AVAILABILITY_BASE_KEY,
  RESERVE_CHECKOUT_BASE_KEY,
  RESERVE_CURRENT_RESERVATIONS_BASE_KEY,
  RESERVE_PAST_RESERVATIONS_BASE_KEY,
  RESERVE_RATES_BASE_KEY,
  SETUP_INTENT_BASE_KEY,
} from "./keys"

export const calendarsQueryOptions = (args: { locationID: string }) => {
  return queryOptions({
    queryKey: [RESERVE_BASE_KEY, CALENDARS_BASE_KEY, args.locationID],
    queryFn: async (): Promise<BookingCalendarMini[]> => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      const rsp = await api.reserve.calendars.POST({
        cognitoIDToken: cognitoIDToken || "",
      })
      if ("failReason" in rsp) throw new ReserveCalendarsError(rsp.failReason)
      return rsp.calendars
    },
    retry: false,
  })
}

export const calendarAvailabilityQueryOptions = (args: {
  bookingCalendarID: string
  month: string // YYYY-MM
}) => {
  return queryOptions({
    queryKey: [
      RESERVE_BASE_KEY,
      RESERVE_CALENDAR_AVAILABILITY_BASE_KEY,
      args.bookingCalendarID,
      args.month,
    ],
    queryFn:
      async (): Promise<ReserveCalendarAvailabilityPOSTSuccessResponse> => {
        const cognitoIDToken = await api.auth.getCognitoIDToken()
        const rsp = await api.reserve.calendarAvailability.POST({
          bookingCalendarID: args.bookingCalendarID,
          cognitoIDToken: cognitoIDToken || "",
          month: args.month,
        })
        if ("failReason" in rsp)
          throw new ReserveCalendarAvailabilityError(rsp.failReason)
        return rsp
      },
    retry: false,
    gcTime: 0,
  })
}

export const ratesQueryOptions = (args: {
  bookingCalendarID: string
  year: string
  month: string
  day: string
  endDate?: string
}) => {
  return queryOptions({
    queryKey: [RESERVE_BASE_KEY, RESERVE_RATES_BASE_KEY, args],
    queryFn: async (): Promise<InventoryTreeRateMini[]> => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      const dateOnly = `${args.year}-${args.month.padStart(2, "0")}-${args.day.padStart(2, "0")}`
      const rsp = await api.reserve.calendarRates.POST({
        cognitoIDToken: cognitoIDToken || "",
        dateOnly,
        endDate: args.endDate,
      })
      if ("failReason" in rsp)
        throw new ReserveCalendarRatesError(rsp.failReason)
      return Object.values(rsp.rates)
    },
    retry: false,
    gcTime: 0,
  })
}

export async function selectReserveRate(
  inventoryTreeRateMiniID: string
): Promise<void> {
  const cognitoIDToken = await api.auth.getCognitoIDToken()
  const rsp = await api.reserve.selectRate.POST({
    cognitoIDToken: cognitoIDToken || "",
    inventoryTreeRateMiniID,
  })
  if ("failReason" in rsp) throw new ReserveSelectRateError(rsp.failReason)
}

export async function submitReservePlate(args: {
  plate: string | null
  plateEntrySkipped: boolean
}): Promise<void> {
  const rsp = await api.reserve.plate.POST(args)
  if ("failReason" in rsp) throw new ReservePlateError(rsp.failReason)
}

export const reserveCheckoutQueryOptions = () => {
  return queryOptions({
    queryKey: [RESERVE_BASE_KEY, RESERVE_CHECKOUT_BASE_KEY],
    queryFn: async (): Promise<ReserveCheckoutPOSTSuccessResponse> => {
      const rsp = await api.reserve.checkout.POST({})
      if ("failReason" in rsp) throw new ReserveCheckoutError(rsp.failReason)
      return rsp
    },
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export const applyReservePromoMutationOptions = (queryClient: QueryClient) => ({
  mutationFn: async (promoCode: string) => {
    if (!promoCode) throw new ReservePromoError(PromoFailReason.InvalidCode)
    const cognitoIDToken = await api.auth.getCognitoIDToken()
    const rsp = await api.reserve.promoPayment.POST({
      cognitoIDToken: cognitoIDToken || "",
      promoCode,
    })
    if ("failReason" in rsp) throw new ReservePromoError(rsp.failReason)
    return rsp
  },
  onSuccess: (data: ReservePromoPaymentPOSTSuccessResponse) =>
    queryClient.setQueryData(reserveCheckoutQueryOptions().queryKey, (prev) =>
      prev ? { ...prev, transactionDetails: data.transactionDetails } : prev
    ),
})

export const currentReservationsQueryOptions = (
  locationID: string | null = null
) => {
  return queryOptions({
    queryKey: [
      ME_BASE_KEY,
      RESERVE_BASE_KEY,
      RESERVE_CURRENT_RESERVATIONS_BASE_KEY,
      locationID,
    ],
    queryFn: async (): Promise<ReservationMini[]> => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      const rsp = await api.reserve.currentReservations.POST({
        cognitoIDToken: cognitoIDToken || "",
        locationID,
      })
      if ("failReason" in rsp)
        throw new ReserveCurrentReservationsError(rsp.failReason)
      return rsp.reservations
    },
    retry: false,
  })
}

export const pastReservationsQueryOptions = (
  locationID: string | null = null
) => {
  return queryOptions({
    queryKey: [
      ME_BASE_KEY,
      RESERVE_BASE_KEY,
      RESERVE_PAST_RESERVATIONS_BASE_KEY,
      locationID,
    ],
    queryFn: async (): Promise<ReservationMini[]> => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      const rsp = await api.reserve.pastReservations.POST({
        cognitoIDToken: cognitoIDToken || "",
        locationID,
      })
      if ("failReason" in rsp)
        throw new ReservePastReservationsError(rsp.failReason)
      return rsp.reservations
    },
    retry: false,
  })
}

export const cancelReservationMutationOptions = (
  queryClient: QueryClient,
  locationID: string | null = null
): UseMutationOptions<
  void,
  Error,
  { reservationID: string; enterpriseID: string; locationID: string }
> => ({
  mutationFn: async (args) => {
    const cognitoIDToken = await api.auth.getCognitoIDToken()
    const rsp = await api.reserve.cancelReservation.POST({
      cognitoIDToken: cognitoIDToken || "",
      reservationID: args.reservationID,
      locationID: args.locationID,
      enterpriseID: args.enterpriseID,
    })
    if ("failReason" in rsp)
      throw new ReserveCancelReservationError(rsp.failReason)
  },
  onMutate: async () => {
    await queryClient.cancelQueries({
      queryKey: currentReservationsQueryOptions(locationID).queryKey,
    })
  },
  onSuccess: (_data, { reservationID }) => {
    queryClient.setQueryData<ReservationMini[]>(
      currentReservationsQueryOptions(locationID).queryKey,
      (prev) => prev?.filter((r) => r.reservationID !== reservationID)
    )
  },
  onSettled: () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: currentReservationsQueryOptions(locationID).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: pastReservationsQueryOptions(locationID).queryKey,
      }),
    ]),
})

type UpdateReservationPlateArgs = {
  newPlate: string
  reservationID: string
  receiptID: string
}

export const updateReservationPlateMutationOptions = (
  queryClient: QueryClient,
  locationID: string | null = null
): UseMutationOptions<void, Error, UpdateReservationPlateArgs> => ({
  mutationFn: async (args) => {
    const rsp = await api.reserve.plateUpdate.POST(args)
    if ("failReason" in rsp) throw new ReservePlateUpdateError(rsp.failReason)
  },
  onSuccess: () =>
    queryClient.invalidateQueries({
      queryKey: currentReservationsQueryOptions(locationID).queryKey,
    }),
})

export const reservePaymentIntentQueryOptions = () => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      RESERVE_BASE_KEY,
      PAYMENT_INTENT_BASE_KEY,
    ],
    queryFn: async (): Promise<ReservePaymentIntentPOSTSuccessResponse> => {
      const [cognitoIDToken, userAttributes] = await Promise.all([
        api.auth.getCognitoIDToken(),
        api.auth.fetchUserAttributes(),
      ])

      const email = userAttributes.email || null
      const phone = userAttributes.phone_number || null
      const contactType = email ? ContactType.Email : ContactType.Phone

      const rsp = await api.reserve.paymentIntent.POST({
        cognitoIDToken: cognitoIDToken || "",
        phone,
        email,
        contactType,
      })
      if ("failReason" in rsp) {
        if (rsp.failReason === CheckoutFailReason.CognitoTokenExpired) {
          await api.auth.signOut()
        }
        throw new ReservePaymentIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmReservePaymentQueryOptions = () => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      RESERVE_BASE_KEY,
      CONFIRM_PAYMENT_BASE_KEY,
    ],
    queryFn: async (): Promise<ReserveConfirmPaymentPOSTSuccessResponse> => {
      const rsp = await api.reserve.confirmPayment.POST({})
      if ("failReason" in rsp) {
        throw new ReserveConfirmPaymentError(rsp.failReason)
      }
      return rsp
    },
    retry: (failureCount, error) => {
      if (failureCount >= 8) return false
      if (error instanceof ReserveConfirmPaymentError) {
        return ["PaymentWebhookNotCompleted", "SystemError"].includes(
          error.failReason
        )
      }
      return true
    },
    retryDelay: 1000,
  })
}

export const reserveSetupIntentQueryOptions = () => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      RESERVE_BASE_KEY,
      SETUP_INTENT_BASE_KEY,
    ],
    queryFn: async (): Promise<ReserveSetupIntentPOSTSuccessResponse> => {
      const [cognitoIDToken, userAttributes] = await Promise.all([
        api.auth.getCognitoIDToken(),
        api.auth.fetchUserAttributes(),
      ])

      const email = userAttributes.email || null
      const phone = userAttributes.phone_number || null
      const contactType = email ? ContactType.Email : ContactType.Phone

      const rsp = await api.reserve.setupIntent.POST({
        cognitoIDToken: cognitoIDToken || "",
        phone,
        email,
        contactType,
      })
      if ("failReason" in rsp) {
        throw new ReserveSetupIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmReserveSetupIntentQueryOptions = () => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      RESERVE_BASE_KEY,
      CONFIRM_SETUP_INTENT_BASE_KEY,
    ],
    queryFn:
      async (): Promise<ReserveConfirmSetupIntentPOSTSuccessResponse> => {
        const rsp = await api.reserve.confirmSetupIntent.POST({})
        if ("failReason" in rsp) {
          throw new ReserveConfirmSetupIntentError(rsp.failReason)
        }
        return rsp
      },
    retry: (failureCount, error) => {
      if (failureCount >= 8) return false
      if (error instanceof ReserveConfirmSetupIntentError) {
        return ["WebhookNotCompleted", "SystemError"].includes(error.failReason)
      }
      return true
    },
    retryDelay: 1000,
  })
}
